import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { 
    getCurrentAnnualTournament,
    getAnnualTournaments,
    getAnnualWinners,
    getAnnualTournamentStats,
    getAnnualTournamentLeaderboard,
    registerForAnnualTournament,
    unregisterFromAnnualTournament,
    getRegistrationStatus
} from '../api/annualTournaments';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbModal from '../components/controls/HrnbModal';
import { DetailsIcon, RejectIcon, RegisterIcon } from '../icons';
import AnnualTournamentCard from '../components/tournaments/AnnualTournamentCard';
import AnnualWinnersList from '../components/tournaments/AnnualWinnersList';
import '../css/pages/AnnualTournamentsPage.css';

/**
 * Страница ежегодных турниров
 * Отображает текущий активный турнир и победителей прошлых лет
 */
function AnnualTournamentsPage() {
    const { currentUser } = useContext(UserContext);
    
    // Состояния
    const [currentTournament, setCurrentTournament] = useState(null);
    const [allTournaments, setAllTournaments] = useState([]);
    const [winners, setWinners] = useState([]);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [selectedYear, setSelectedYear] = useState(null);
    const [isRegistered, setIsRegistered] = useState(false);

    // Загрузка данных
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Загрузка текущего турнира
            const current = await getCurrentAnnualTournament();
            console.log('[AnnualTournamentsPage] Текущий турнир:', current);
            setCurrentTournament(current);

            // Загрузка всех турниров для отображения списка
            const tournaments = await getAnnualTournaments();
            setAllTournaments(tournaments);

            // Загрузка победителей
            try {
                const winnersList = await getAnnualWinners();
                setWinners(winnersList);
                console.log('[AnnualTournamentsPage] Победители загружены:', winnersList.length);
            } catch (winnersErr) {
                console.warn('[AnnualTournamentsPage] Не удалось загрузить победителей:', winnersErr);
            }

            // Загрузка статистики и лидерборда для текущего турнира
            if (current) {
                try {
                    const statsData = await getAnnualTournamentStats(current.year);
                    setStats(statsData);
                } catch (err) {
                    console.warn('[AnnualTournamentsPage] Не удалось загрузить статистику:', err);
                }

                try {
                    const leaderboardData = await getAnnualTournamentLeaderboard(current.year, { size: 5 });
                    setLeaderboard(leaderboardData);
                } catch (err) {
                    console.warn('[AnnualTournamentsPage] Не удалось загрузить лидерборд:', err);
                }
            }

            // Проверка регистрации текущего пользователя
            if (currentUser && current) {
                try {
                    const status = await getRegistrationStatus(current.year);
                    setIsRegistered(!!status.isRegistered);
                } catch (err) {
                    console.warn('[AnnualTournamentsPage] Не удалось проверить статус регистрации');
                }
            }
        } catch (err) {
            console.error('Ошибка при загрузке ежегодных турниров:', err);
            setError('Не удалось загрузить данные. Попробуйте позже.');
        } finally {
            setLoading(false);
        }
    };

    // Регистрация на турнир
    const handleRegister = async () => {
        if (!selectedYear || !currentUser) return;

        setRegistering(true);
        try {
            await registerForAnnualTournament(selectedYear);
            setShowRegisterModal(false);
            setSelectedYear(null);
            setRegistering(false);
            setIsRegistered(true);
            loadData();
        } catch (err) {
            console.error('Ошибка регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось зарегистрироваться');
            setRegistering(false);
        }
    };

    // Отмена регистрации
    const handleUnregister = async (year) => {
        if (!year || !currentUser) return;

        try {
            await unregisterFromAnnualTournament(year);
            setIsRegistered(false);
            loadData();
        } catch (err) {
            console.error('Ошибка отмены регистрации:', err);
            setError(err.response?.data?.message || 'Не удалось отменить регистрацию');
        }
    };

    const openRegisterModal = (year) => {
        setSelectedYear(year);
        setShowRegisterModal(true);
    };

    if (loading) {
        return (
            <div className="annual-tournaments-page">
                <HrnbLoader />
            </div>
        );
    }

    return (
        <div className="annual-tournaments-page">
            <div className="annual-tournaments-page__header">
                <h1 className="annual-tournaments-page__title">Ежегодные турниры</h1>
                <p className="annual-tournaments-page__subtitle">
                    Главные соревнования года с лучшими артистами
                </p>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {/* Текущий турнир */}
            {currentTournament && (
                <section className="annual-tournaments-page__current">
                    <h2 className="annual-tournaments-page__section-title">
                        Текущий турнир
                    </h2>
                    <AnnualTournamentCard
                        tournament={currentTournament}
                        currentUser={currentUser}
                        isRegistered={isRegistered}
                        onRegister={() => openRegisterModal(currentTournament.year)}
                        onUnregister={() => handleUnregister(currentTournament.year)}
                        registering={registering}
                    />
                </section>
            )}

            {/* Статистика текущего турнира */}
            {currentTournament && stats && (
                <section className="annual-tournaments-page__stats">
                    <h2 className="annual-tournaments-page__section-title">
                        Статистика турнира {currentTournament.year}
                    </h2>
                    <div className="annual-tournaments-page__stats-grid">
                        <div className="annual-tournaments-page__stat-card">
                            <div className="annual-tournaments-page__stat-value">
                                {stats.totalParticipants ?? currentTournament.participantCount ?? 0}
                            </div>
                            <div className="annual-tournaments-page__stat-label">
                                Участников
                            </div>
                        </div>

                        <div className="annual-tournaments-page__stat-card">
                            <div className="annual-tournaments-page__stat-value">
                                {stats.totalMatches ?? 0}
                            </div>
                            <div className="annual-tournaments-page__stat-label">
                                Матчей
                            </div>
                        </div>

                        <div className="annual-tournaments-page__stat-card">
                            <div className="annual-tournaments-page__stat-value">
                                {stats.totalVotes ?? 0}
                            </div>
                            <div className="annual-tournaments-page__stat-label">
                                Голосов
                            </div>
                        </div>

                        {stats.totalRounds != null && (
                            <div className="annual-tournaments-page__stat-card">
                                <div className="annual-tournaments-page__stat-value">
                                    {stats.totalRounds}
                                </div>
                                <div className="annual-tournaments-page__stat-label">
                                    Раундов
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Лидерборд текущего турнира */}
            {currentTournament && leaderboard?.content && leaderboard.content.length > 0 && (
                <section className="annual-tournaments-page__leaderboard">
                    <h2 className="annual-tournaments-page__section-title">
                        Лидерборд {currentTournament.year}
                    </h2>
                    <div className="annual-tournaments-page__leaderboard-list">
                        {leaderboard.content.slice(0, 5).map((entry, index) => {
                            const rank = entry.rank || index + 1;
                            const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

                            return (
                                <div key={entry.profileId || index} className="annual-tournaments-page__leaderboard-item">
                                    <span className="annual-tournaments-page__leaderboard-rank">
                                        {rankIcon}
                                    </span>
                                    <span className="annual-tournaments-page__leaderboard-name">
                                        {entry.displayName || entry.profileName || 'Аноним'}
                                    </span>
                                    <span className="annual-tournaments-page__leaderboard-stats">
                                        {entry.wins ?? 0} побед / {entry.matches ?? 0} матчей
                                    </span>
                                    <span className="annual-tournaments-page__leaderboard-points">
                                        🏆 {entry.points ?? entry.score ?? 0} очк.
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="annual-tournaments-page__leaderboard-more">
                        <Link to={`/tournaments/annual/${currentTournament.id || currentTournament.year}`}>
                            <HrnbButton variant="secondary" icon={<DetailsIcon />}>
                                Полный лидерборд
                            </HrnbButton>
                        </Link>
                    </div>
                </section>
            )}

            {/* Все турниры */}
            {allTournaments.length > 0 && !currentTournament && (
                <section className="annual-tournaments-page__list">
                    <h2 className="annual-tournaments-page__section-title">
                        Все турниры
                    </h2>
                    <div className="annual-tournaments-page__cards">
                        {allTournaments.map((tournament) => (
                            <AnnualTournamentCard
                                key={tournament.id}
                                tournament={tournament}
                                currentUser={currentUser}
                                isRegistered={isRegistered}
                                onRegister={() => openRegisterModal(tournament.year)}
                                onUnregister={() => handleUnregister(tournament.year)}
                                registering={registering}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Победители прошлых лет */}
            {winners.length > 0 && (
                <section className="annual-tournaments-page__winners">
                    <h2 className="annual-tournaments-page__section-title">
                        Победители прошлых лет
                    </h2>
                    <AnnualWinnersList winners={winners} />
                </section>
            )}

            {/* Нет активных турниров */}
            {!currentTournament && allTournaments.length === 0 && (
                <div className="annual-tournaments-page__empty">
                    <p className="annual-tournaments-page__empty-text">
                        В данный момент нет ежегодных турниров
                    </p>
                </div>
            )}

            {/* Модальное окно регистрации */}
            <HrnbModal
                show={showRegisterModal}
                onHide={() => setShowRegisterModal(false)}
                title={`Регистрация на турнир ${selectedYear}`}
            >
                <div className="annual-tournaments-page__modal">
                    <p>
                        Вы собираетесь зарегистрироваться на ежегодный турнир {selectedYear} года.
                    </p>
                    <p className="annual-tournaments-page__modal-warning">
                        Внимание: регистрация окончательна и не может быть отменена после начала турнира.
                    </p>
                    
                    <div className="annual-tournaments-page__modal-actions">
                        <HrnbButton
                            variant="secondary"
                            onClick={() => setShowRegisterModal(false)}
                            icon={<RejectIcon />}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="primary"
                            onClick={handleRegister}
                            disabled={registering}
                            icon={<RegisterIcon />}
                        >
                            {registering ? 'Регистрация...' : 'Зарегистрироваться'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>
        </div>
    );
}

export default AnnualTournamentsPage;
